// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
/**
 * Leads API service using centralized API instance
 * Uses api/v1 prefix for leads endpoints
 */
import api from './api'
import { saveBlob } from '../../lib/download'
import type {
  DetailedLead,
  CreateLeadData,
  UpdateLeadData,
  LeadsResponse,
  LeadFilters,
  Lead
} from '@crm-src/types/lead'

/**
 * `prospect_source` values stamped on prospects created by the autonomous XOP
 * sales agent ("Convert to Prospect" in the dashboard's Sales ▸ Leads). The
 * serializer maps `prospect_source` → `Lead.source`, so the classifier reads
 * `lead.source`. Kept in lockstep with the crm-api's `AI_PROSPECT_SOURCES`
 * (LeadsController) and the dashboard server's `AI_PROSPECT_SOURCE`
 * (xop-edge-staff apps/server/src/routes/sales.ts). "XOP Sales Agent" is the
 * earlier value, still recognised so already-converted prospects classify.
 */
export const AI_PROSPECT_SOURCES = ['AI created prospect', 'XOP Sales Agent'] as const

/** True when a prospect was generated by the autonomous sales agent. */
export function isAiProspect(lead: Pick<Lead, 'source'> | null | undefined): boolean {
  const source = lead?.source
  return source != null && (AI_PROSPECT_SOURCES as readonly string[]).includes(source)
}


export const leadsApi = {
  /**
   * Get leads with optional filters and pagination
   */
  getAll: async (filters?: LeadFilters): Promise<LeadsResponse> => {
    const params = new URLSearchParams()

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value))
        }
      })
    }

    const response = await api.get(`/leads?${params.toString()}`)
    return response.data
  },

  /**
   * Get single lead by ID with detailed information
   */
  getById: async (id: string): Promise<{ lead: DetailedLead }> => {
    const response = await api.get(`/leads/${id}`)
    return response.data
  },

  /**
   * Create new lead
   */
  create: async (leadData: CreateLeadData): Promise<{ lead: DetailedLead; message: string }> => {
    const response = await api.post('/leads', { lead: leadData })
    return response.data
  },

  /**
   * Update existing lead
   */
  update: async (id: string, leadData: UpdateLeadData): Promise<{ lead: DetailedLead; message: string }> => {
    const response = await api.put(`/leads/${id}`, { lead: leadData })
    return response.data
  },

  /**
   * Delete lead (soft delete)
   */
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/leads/${id}`)
    return response.data
  },

  /**
   * Bulk delete leads
   */
  bulkDelete: async (leadIds: string[]): Promise<{
    message: string,
    deleted_count: number,
    failed_deletions?: Array<{
      id: string,
      name: string,
      company?: string,
      error: string
    }>
  }> => {
    const response = await api.delete('/leads/bulk_delete', {
      data: { lead_ids: leadIds }
    })

    return {
      message: response.data.message || 'Prospects processed successfully',
      deleted_count: response.data.deleted_count || 0,
      failed_deletions: response.data.failed_deletions || []
    }
  },

  /**
   * Bulk assign salespeople to leads
   */
  bulkAssignSalespeople: async (leadIds: string[], salespeopleIds: string[]): Promise<{
    message: string,
    assigned_count: number,
    total_assignments: number,
    failed_assignments?: Array<{
      id: string,
      name: string,
      error: string
    }>
  }> => {
    const response = await api.post('/leads/bulk_assign_salespeople', {
      lead_ids: leadIds,
      salesperson_ids: salespeopleIds
    })
    return response.data
  },

  /**
   * Bulk assign reseller to leads
   */
  bulkAssignReseller: async (leadIds: string[], resellerId: string): Promise<{
    message: string,
    assigned_count: number,
    failed_assignments?: Array<{
      id: string,
      name: string,
      error: string
    }>
  }> => {
    const response = await api.post('/leads/bulk_assign_reseller', {
      lead_ids: leadIds,
      reseller_id: resellerId
    })
    return response.data
  },

  /**
   * Convert lead to contact and deal
   */
  convert: async (id: string): Promise<{
    message: string;
    contact: { id: string; name: string; email: string };
    deal: { id: string; name: string; amount: number; stage: string };
    lead: { id: string; status: string };
    follow_up_task?: {
      id: string;
      due_date: string;
      title: string;
      notes?: string;
    };
  }> => {
    const response = await api.post(`/leads/${id}/convert`)
    return response.data
  },

  /**
   * Import leads from JSON data
   */
  import: async (leads: any[]): Promise<{
    imported: number;
    total: number;
    errors: string[];
    success_rate: number;
  }> => {
    const importTimeout = Math.max(60000, leads.length * 1000 + 30000)

    const response = await api.post('/leads/import', { leads }, {
      timeout: importTimeout,
    })
    return response.data
  },

  /**
   * Download export file as CSV or XLSX with proper filename
   */
  downloadExport: async (format: 'csv' | 'xlsx', filters?: LeadFilters, filename?: string): Promise<void> => {
    const params = new URLSearchParams()

    if (filters) {
      const exportFilters = { ...filters } as any
      delete exportFilters.page
      delete exportFilters.per_page
      delete exportFilters.include_stats

      Object.entries(exportFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value))
        }
      })
    }

    params.append('format', format)

    const response = await api.get(`/leads/export?${params.toString()}`, {
      responseType: 'blob'
    })

    const blob = response.data
    const defaultFilename = `prospects_export_${new Date().toISOString().split('T')[0]}.${format}`

    // Native OS Save-As dialog in the desktop app; browser download on the web.
    await saveBlob(filename || defaultFilename, blob)
  }
}

export default leadsApi
